import { useCallback, useEffect, useState } from "react";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { PasoPlantilla } from "@/components/migracion/PasoPlantilla";
import { PasoSubir } from "@/components/migracion/PasoSubir";
import { ResumenLote } from "@/components/migracion/ResumenLote";
import { TablaFilas } from "@/components/migracion/TablaFilas";
import { ResumenCreditos } from "@/components/migracion/ResumenCreditos";
import { TablaCreditos } from "@/components/migracion/TablaCreditos";
import { TablaCorrecciones } from "@/components/migracion/TablaCorrecciones";
import { DeclaracionConfirmar } from "@/components/migracion/DeclaracionConfirmar";
import { ProgresoLote } from "@/components/migracion/ProgresoLote";
import { HistorialLotes } from "@/components/migracion/HistorialLotes";
import { api, apiError } from "@/lib/api";
import { ESTADOS_CONTINUAR, descargarArchivo } from "@/lib/migracion";

// El lote que se muestra al entrar: el que corre, uno detenido/interrumpido o el recién validado.
const loteInicial = (lotes) =>
  (lotes.find((l) => l.estado === "en_proceso")
    || (lotes[0] && [...ESTADOS_CONTINUAR, "validado"].includes(lotes[0].estado) ? lotes[0] : null))?.id || null;

export default function MigracionCartera() {
  const [lotes, setLotes] = useState([]);
  const [activoId, setActivoId] = useState(null);
  const [lote, setLote] = useState(null);
  const [error, setError] = useState("");

  const cargarLotes = useCallback(async (elegir = false) => {
    try {
      const r = await api.get("/migracion-cartera/lotes");
      setLotes(r.data);
      if (elegir) setActivoId(loteInicial(r.data));
    } catch (e) {
      setError(apiError(e));
    }
  }, []);

  const cargarLote = useCallback(async (id) => {
    if (!id) return setLote(null);
    try {
      setLote((await api.get(`/migracion-cartera/lotes/${id}`)).data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { cargarLotes(true); }, [cargarLotes]);
  useEffect(() => { cargarLote(activoId); }, [activoId, cargarLote]);

  // Sondeo cada 4 s mientras el lote está en proceso (patrón de CentroListas).
  const enProceso = lote?.estado === "en_proceso";
  useEffect(() => {
    if (!enProceso) return undefined;
    const t = setInterval(async () => {
      const r = await api.get(`/migracion-cartera/lotes/${lote.id}`).catch(() => null);
      if (!r) return;
      setLote(r.data);
      if (r.data.estado !== "en_proceso") cargarLotes();
    }, 4000);
    return () => clearInterval(t);
  }, [enProceso, lote?.id, cargarLotes]);

  const refrescar = () => { cargarLote(activoId); cargarLotes(); };
  const hayEnCurso = lotes.some((l) => l.estado === "en_proceso");

  if (error) {
    return (
      <div>
        <PageHeader title="Migración de cartera" subtitle="Clientes y créditos de tu sistema anterior por Excel" />
        <p className="p-8 text-sm text-red-700" data-testid="migracion-error-acceso">{error}</p>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Migración de cartera" subtitle="Clientes (personas físicas) y créditos de tu sistema anterior por Excel" />
      <div className="space-y-5 p-6 md:p-8" data-testid="migracion-cartera-page">
        <PasoPlantilla />
        <PasoSubir enCurso={hayEnCurso} onValidado={(id) => { setActivoId(id); cargarLotes(); }} />
        {lote && (
          <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-5" data-testid="migracion-lote-detalle">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                Resultado de la validación <span className="ml-1 font-mono text-xs font-normal text-slate-400">{lote.id} · {lote.nombre_archivo}</span>
              </h2>
              <Button variant="outline" size="sm" className="gap-2" data-testid="migracion-descargar-reporte"
                onClick={() => descargarArchivo(`/migracion-cartera/lotes/${lote.id}/errores.xlsx`, `reporte_${lote.id}.xlsx`).catch((e) => toast.error(apiError(e)))}>
                <Download className="h-4 w-4" /> Descargar reporte
              </Button>
            </div>
            <p className="text-sm font-semibold text-slate-800">Clientes</p>
            <ResumenLote lote={lote} />
            <TablaFilas filas={(lote.filas || []).filter((f) => !f.hoja)} />
            <ResumenCreditos lote={lote} />
            <TablaCreditos filas={(lote.filas || []).filter((f) => f.hoja === "Creditos")} />
            <TablaCorrecciones filas={(lote.filas || []).filter((f) => f.hoja === "Corregir creditos")} />
          </section>
        )}
        {lote?.estado === "validado" && <DeclaracionConfirmar lote={lote} onLanzado={refrescar} />}
        {lote && lote.estado !== "validado" && <ProgresoLote lote={lote} onCambio={refrescar} />}
        <HistorialLotes lotes={lotes} activo={activoId} onVer={setActivoId} />
      </div>
    </div>
  );
}
